import enMessages from '../../../../translations/en.json';
import thMessages from '../../../../translations/th.json';
import frMessages from '../../../../translations/fr.json';
import deMessages from '../../../../translations/de.json';
import esMessages from '../../../../translations/es.json';
import { getProcess, resolveLatestProcessName } from '../../../../transactions/transaction';
import { PAYMENT_METHODS } from '../../../../util/enums';

const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = [DEFAULT_LOCALE, 'th', 'fr', 'de', 'es'];
const MESSAGES_BY_LOCALE = {
  en: enMessages,
  th: thMessages,
  fr: frMessages,
  de: deMessages,
  es: esMessages,
};

const normalizeLocale = locale => {
  const value = String(locale || '').toLowerCase();
  const base = value.split('-')[0];

  if (SUPPORTED_LOCALES.includes(value)) {
    return value;
  }

  if (SUPPORTED_LOCALES.includes(base)) {
    return base;
  }

  return DEFAULT_LOCALE;
};

const normalizeId = value => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && value.uuid) {
    return value.uuid;
  }

  return null;
};

const interpolate = (template, values = {}) => {
  return String(template || '')
    .replace(/\{\s*([a-zA-Z0-9_]+)\s*\}/g, (_, key) => {
      const value = values[key];
      return value == null ? '' : String(value);
    })
    .replace(/\s+/g, ' ')
    .trim();
};

const getLocalizedMessage = ({ id, locale, values }) => {
  if (!id) {
    return null;
  }

  const normalizedLocale = normalizeLocale(locale);
  const template =
    MESSAGES_BY_LOCALE[normalizedLocale]?.[id] || MESSAGES_BY_LOCALE[DEFAULT_LOCALE]?.[id] || null;

  if (!template) {
    return null;
  }

  return interpolate(template, values);
};

const serializeLocalizedMap = localizedValues => {
  const normalized = SUPPORTED_LOCALES.reduce((collected, locale) => {
    const value = localizedValues?.[locale];
    if (!value) {
      return collected;
    }

    return {
      ...collected,
      [locale]: String(value),
    };
  }, {});

  return Object.keys(normalized).length ? JSON.stringify(normalized) : null;
};

const pickLocalizedValueForLocale = (value, locale) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const normalizedLocale = normalizeLocale(locale);
    const lowered = Object.entries(value).reduce((collected, [key, val]) => {
      return {
        ...collected,
        [String(key).toLowerCase()]: val,
      };
    }, {});

    return (
      lowered[normalizedLocale] ||
      lowered[DEFAULT_LOCALE] ||
      lowered.th ||
      lowered.fr ||
      lowered.de ||
      lowered.es ||
      Object.values(lowered).find(Boolean) ||
      null
    );
  }

  return null;
};

const getListingTitleLocalized = listing => {
  const title = listing?.attributes?.title;
  const thLanguage = listing?.attributes?.publicData?.thLanguage;
  const fallbackTitle =
    pickLocalizedValueForLocale(title, DEFAULT_LOCALE) ||
    (typeof title === 'string' ? title : null);
  const thaiTitle =
    thLanguage?.th?.title ||
    thLanguage?.TH?.title ||
    pickLocalizedValueForLocale(title, 'th') ||
    null;

  const titleByLocale = SUPPORTED_LOCALES.reduce((collected, locale) => {
    const localizedTitle =
      locale === 'th'
        ? thaiTitle || fallbackTitle
        : pickLocalizedValueForLocale(title, locale) || fallbackTitle || thaiTitle;

    return localizedTitle
      ? {
          ...collected,
          [locale]: localizedTitle,
        }
      : collected;
  }, {});

  return Object.keys(titleByLocale).length ? titleByLocale : null;
};

const getBookingData = transaction => {
  const booking = transaction?.booking?.attributes || {};
  const protectedData = transaction?.attributes?.protectedData || {};

  return {
    bookingStartAt: booking.start || protectedData.bookingStartAt || null,
    bookingEndAt: booking.end || protectedData.bookingEndAt || null,
    bookingTimezone: booking.timeZone || booking.timezone || protectedData.bookingTimezone || null,
  };
};

const getParticipantDisplayName = participant => {
  const profile = participant?.attributes?.profile || {};
  return (
    profile.displayName ||
    [profile.firstName, profile.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    null
  );
};

const getRoleForUser = ({ transaction, currentUserId }) => {
  const providerId = normalizeId(transaction?.provider?.id);
  const customerId = normalizeId(transaction?.customer?.id);

  if (currentUserId && currentUserId === providerId) {
    return 'provider';
  }

  if (currentUserId && currentUserId === customerId) {
    return 'customer';
  }

  return null;
};

const getPaymentMethod = transaction => {
  return String(transaction?.attributes?.protectedData?.paymentMethod || '')
    .trim()
    .toLowerCase();
};

const isPayLaterBooking = ({ processName, transaction }) => {
  const paymentMethod = getPaymentMethod(transaction);
  return (
    processName === 'mobile-banking' ||
    processName === 'promptpay' ||
    paymentMethod === PAYMENT_METHODS.PROMPTPAY ||
    paymentMethod === PAYMENT_METHODS.MOBILE_BANKING ||
    paymentMethod === 'mobile-banking'
  );
};

const resolveFallbackConfig = ({ transaction, processName, role, processState }) => {
  if (
    role === 'customer' &&
    processState === 'pending-payment' &&
    getPaymentMethod(transaction) === PAYMENT_METHODS.CARD
  ) {
    return null;
  }

  if (processState === 'inquiry') {
    if (role !== 'provider') {
      return null;
    }

    return {
      type: 'booking.requested',
      titleId: 'Notifications.inquiryReceived.title',
      snippetId: 'Notifications.inquiryReceived.title',
    };
  }

  if (role === 'provider' && processState === 'preauthorized') {
    if (isPayLaterBooking({ processName, transaction })) {
      return {
        type: 'booking.secured',
        titleId: 'Notifications.bookingSecured.title',
        snippetId: 'Notifications.bookingSecured.title',
      };
    }

    return {
      type: 'booking.requested',
      titleId: 'Notifications.bookingRequestReceived.title',
      snippetId: 'Notifications.bookingRequestReceived.title',
    };
  }

  if (role === 'customer' && processState === 'accepted-pending-payment') {
    return {
      type: 'booking.accepted',
      titleId: 'Notifications.bookingAcceptedPendingPaymentCustomer.title',
      snippetId: 'Notifications.bookingAcceptedPendingPaymentCustomer.title',
    };
  }

  if (role === 'provider' && processState === 'accepted-pending-payment') {
    return {
      type: 'booking.accepted',
      titleId: 'Notifications.bookingAcceptedPendingPaymentProvider.title',
      snippetId: 'Notifications.bookingAcceptedPendingPaymentProvider.title',
    };
  }

  if (role === 'customer' && processState === 'accepted') {
    if (isPayLaterBooking({ processName, transaction })) {
      return {
        type: 'booking.secured',
        titleId: 'Notifications.bookingPaymentReceivedCustomer.title',
        snippetId: 'Notifications.bookingPaymentReceivedCustomer.title',
      };
    }

    return {
      type: 'booking.secured',
      titleId: 'Notifications.bookingCardSecuredCustomer.title',
      snippetId: 'Notifications.bookingCardSecuredCustomer.title',
    };
  }

  if (role === 'provider' && processState === 'accepted') {
    return {
      type: 'booking.secured',
      titleId: 'Notifications.bookingSecured.title',
      snippetId: 'Notifications.bookingSecured.title',
    };
  }

  if (
    role === 'customer' &&
    (processState === 'pending-payment' || processState === 'pending-provider-acceptance')
  ) {
    return {
      type: 'booking.requested',
      titleId:
        processName === 'default-purchase'
          ? `TransactionPage.${processName}.${role}.${processState}.title`
          : 'Notifications.bookingRequestSent.title',
      snippetId:
        processName === 'default-purchase'
          ? `TransactionPage.${processName}.${role}.${processState}.title`
          : 'Notifications.bookingRequestSent.title',
    };
  }

  if (
    role === 'provider' &&
    (processState === 'pending-payment' || processState === 'pending-provider-acceptance')
  ) {
    return {
      type: 'booking.requested',
      titleId:
        processName === 'default-purchase'
          ? `TransactionPage.${processName}.${role}.${processState}.title`
          : 'Notifications.bookingRequestReceived.title',
      snippetId:
        processName === 'default-purchase'
          ? `TransactionPage.${processName}.${role}.${processState}.title`
          : 'Notifications.bookingRequestReceived.title',
    };
  }

  if (processName === 'default-purchase' && role === 'provider' && processState === 'purchased') {
    return {
      type: 'booking.requested',
      snippetId: null,
    };
  }

  return null;
};

const buildLocalizedLines = ({ config, processName, role, processState, values }) => {
  const titleId = config?.titleId || `TransactionPage.${processName}.${role}.${processState}.title`;
  const snippetId = config?.snippetId || titleId;

  const titleByLocale = SUPPORTED_LOCALES.reduce((collected, locale) => {
    const localizedTitle = getLocalizedMessage({ id: titleId, locale, values });
    return localizedTitle
      ? {
          ...collected,
          [locale]: localizedTitle,
        }
      : collected;
  }, {});

  const snippetByLocale = SUPPORTED_LOCALES.reduce((collected, locale) => {
    const localizedSnippet = getLocalizedMessage({ id: snippetId, locale, values });
    return localizedSnippet
      ? {
          ...collected,
          [locale]: localizedSnippet,
        }
      : collected;
  }, {});

  const headline =
    titleByLocale[DEFAULT_LOCALE] || titleByLocale.th || Object.values(titleByLocale)[0] || null;
  const snippet =
    snippetByLocale[DEFAULT_LOCALE] ||
    snippetByLocale.th ||
    Object.values(snippetByLocale)[0] ||
    headline;

  if (!headline) {
    return null;
  }

  return {
    headline,
    snippet,
    title_i18n: serializeLocalizedMap(titleByLocale),
    message_i18n: serializeLocalizedMap(snippetByLocale),
  };
};

const mapAttentionTransactionToNotification = ({ transaction, currentUserId }) => {
  const txId = normalizeId(transaction?.id);
  if (!txId) {
    return null;
  }

  const role = getRoleForUser({ transaction, currentUserId });
  if (!role) {
    return null;
  }

  const processAlias = String(transaction?.attributes?.processName || '').trim();
  const processName = resolveLatestProcessName(processAlias.split('/')[0] || processAlias);
  const process = getProcess(processName);
  const processState = process.getState(transaction);
  const config = resolveFallbackConfig({ transaction, processName, role, processState });

  if (!config) {
    return null;
  }

  const customerName = getParticipantDisplayName(transaction?.customer);
  const providerName = getParticipantDisplayName(transaction?.provider);
  const listingTitleLocalized = getListingTitleLocalized(transaction?.listing);
  const values = {
    customerName: customerName || '',
    customerDisplayName: customerName || '',
    providerName: providerName || '',
    providerDisplayName: providerName || '',
    listingTitle: listingTitleLocalized?.en || listingTitleLocalized?.th || '',
  };
  const lines = buildLocalizedLines({
    config,
    processName,
    role,
    processState,
    values,
  });

  if (!lines) {
    return null;
  }

  const lastTransition = String(
    transaction?.attributes?.lastTransition || processState || ''
  ).trim();
  const dedupeKey = `tx:${txId}:${lastTransition || 'legacy-attention'}:legacy-attention`;
  const occurredAt =
    transaction?.attributes?.lastTransitionedAt || transaction?.attributes?.createdAt || null;
  const listingNameI18n = serializeLocalizedMap(listingTitleLocalized);
  const targetUrl = role === 'provider' ? `/sale/${txId}` : `/order/${txId}`;

  return {
    $id: `legacy-attention:${role}:${txId}`,
    dedupeKey,
    headline: lines.headline,
    snippet: lines.snippet,
    title_i18n: lines.title_i18n,
    message_i18n: lines.message_i18n,
    listing: transaction?.listing || null,
    listingName:
      listingTitleLocalized?.[DEFAULT_LOCALE] ||
      listingTitleLocalized?.th ||
      transaction?.listing?.attributes?.title ||
      '',
    listingNameI18n,
    occurredAt,
    isRead: false,
    isLegacyFallback: true,
    recipientUserId: currentUserId || null,
    recipientRole: role,
    source: 'sharetribe',
    targetId: txId,
    targetType: 'transaction',
    targetUrl,
    type: config.type,
    ...getBookingData(transaction),
  };
};

export const mapLegacyAttentionTransactionsToNotifications = ({
  transactions = [],
  currentUser,
}) => {
  const currentUserId = normalizeId(currentUser?.id || currentUser);
  const mapped = transactions
    .map(transaction => mapAttentionTransactionToNotification({ transaction, currentUserId }))
    .filter(Boolean);

  return mapped.sort((a, b) => {
    const aTime = a?.occurredAt ? new Date(a.occurredAt).getTime() : 0;
    const bTime = b?.occurredAt ? new Date(b.occurredAt).getTime() : 0;
    return bTime - aTime;
  });
};
